import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/features/appData";
import "./AppPTOAdjustment.css";
import { CreatePaidTimeOffAdjustmentPayload, EmployeeRecord, PaidTimeOffAdjustment, PaidTimeOffPolicy } from "winslow-core";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { apiGetPaidTimeOffPoliciesForWorkspace } from "../../../services/PaidTimeOffPolicyService";
import { erGetCPValue } from "../app-employee-records/employeeRecordUtils";
import { useNavigate, useParams } from "react-router-dom";
import { apiGetEmployeeRecord, apiGetEmployeeRecordsForWorkspace } from "../../../services/EmployeeRecordService";
import { set } from "lodash";
import { apiCreatePaidTimeOffAdjustment } from "../../../services/PaidTimeOffAdjustmentService";

type AppPTOAdjustmentProps = {
}

export const AppPTOAdjustment = ({  }: AppPTOAdjustmentProps) => {

    const storeUser = useSelector(selectUser);

    const params = useParams();
    const navigate = useNavigate();
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [policies, setPolicies] = useState<PaidTimeOffPolicy[]>([]);
    const [adjustmentType, setAdjustmentType] = useState<'add' | 'subtract'>('add');
    const [employeeRecord, setEmployeeRecord] = useState<EmployeeRecord| undefined>(undefined);
    const [adjustedDays, setAdjustedDays] = useState<number | undefined>(0);
    const [adjustmentData, setAdjustmentData] = useState<Partial<PaidTimeOffAdjustment>>({
        workspaceId: storeUser?.currentWorkspaceId || "",
        employeeRecordId: params.employeeId || "",
        paidTimeOffPolicyId: "",
        adjustedHours: 0,
        isAddition: true,
        noteVisible: "",
        noteAdmin: ""
    });

    useEffect(() => {
        if (params.employeeId) {
            (async () => {
                const er = await apiGetEmployeeRecord(params.employeeId || "");

                if (!er) {
                    toast("That employee can not be found", toastFail);
                    return;
                }

                setEmployeeRecord(er);
            })();
        }
    }, [params.employeeId]);

    useEffect(() => {
        if (storeUser?.currentWorkspaceId) {
            (async () => {
                const thePolicies = await apiGetPaidTimeOffPoliciesForWorkspace(storeUser.currentWorkspaceId || "");

                if (!thePolicies) {
                    toast("No PTO Policies found for this workspace", toastFail);
                    return;
                }

                setPolicies(thePolicies);
                if (thePolicies.length > 0) {
                    setAdjustmentData({ ...adjustmentData, paidTimeOffPolicyId: thePolicies[0].id });
                }


            })();
        }
    }, [storeUser]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!storeUser?.currentWorkspaceId) {
            setError("Workspace information is missing");
            return;
        }

        if (!adjustmentData.paidTimeOffPolicyId) {
            setError("Please select a PTO policy");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        console.log("Submitting adjustment:", adjustmentData);

        setIsSubmitting(false);

        const adjustmentPayload : CreatePaidTimeOffAdjustmentPayload = {
            workspaceId: storeUser.currentWorkspaceId,
            data: adjustmentData,
        }

        console.log("[AppPTOAdjustment] adjustmentPayload: ", adjustmentPayload);
        // return true;
        
        const adjustment = await apiCreatePaidTimeOffAdjustment(adjustmentPayload);

        if (!adjustment) {
            setError("Failed to create adjustment");
            return;
        }

        toast("Adjustment made successfully", toastSuccess);
        window.history.back();
   };


    const firstName = employeeRecord ? (erGetCPValue(employeeRecord, "system-firstName") || "") : "(unknown)";
    const lastName = employeeRecord ? (erGetCPValue(employeeRecord, "system-lastName") || "") : "(unknown)";

    console.log("[AppPTOAdjustment] adjustmentData: ", adjustmentData);

    return (
        <div className="app-pto-adjustment">
            <h1>Adjust Time Available for {firstName} {lastName}</h1>
            <p className="app-pto-adjustment-subtitle">
                Adjust the total time available by choosing to add or subtract hours then entering a number of hours.
                Remember, 8 hours equals 1 day.
            </p>

            <div className="app-pto-adjustment-container">
                <div className="app-pto-form-section">
                    <label htmlFor="policy">PTO POLICY</label>
                    <select
                        id="policy"
                        value={adjustmentData.paidTimeOffPolicyId}
                        onChange={(e) => setAdjustmentData({ ...adjustmentData, paidTimeOffPolicyId: e.target.value })}
                        required
                    >
                        {policies.map(policy => (
                            <option key={policy.id} value={policy.id}>
                                {policy.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="app-pto-form-section">
                    <div className="app-pto-adjustment-type">
                        <input
                            type="radio"
                            name="adjustmentType"
                            checked={adjustmentData.isAddition}
                            className={`adjustment-btn ${adjustmentData.isAddition ? 'active' : ''}`}
                            onChange={(e) => setAdjustmentData({
                                ...adjustmentData, 
                                isAddition: e.target.checked
                            })}
                        />
                        Add
                       
                        <input
                            type="radio"
                            name="adjustmentType"
                            checked={!adjustmentData.isAddition}
                            className={`adjustment-btn ${!adjustmentData.isAddition ? 'active' : ''}`}
                            onChange={(e) => setAdjustmentData({
                                ...adjustmentData, 
                                isAddition: !e.target.checked
                            })}
                        />
                        Subtract
                    </div>
                </div>

                <div className="app-pto-form-section">
                    <label htmlFor="days">Days</label>
                    <input
                        type="number"
                        id="days"
                        // min="0"
                        value={adjustedDays === undefined ? "" : adjustedDays}
                        onChange={(e) => {
                            if (e.target.value !== "") {
                                setAdjustmentData({ 
                                ...adjustmentData,
                                adjustedHours: (parseInt(e.target.value) || 0) * 8
                                });

                                setAdjustedDays(parseInt(e.target.value) || 0);
                            } else {
                                setAdjustedDays(undefined);
                            }
                        }}  
                        required = {true}
                    />
                </div>

                <div className="app-pto-form-section">
                    <label htmlFor="notesEmployee">Notes (Visible to Employee)</label>
                    <textarea
                        id="notesEmployee"
                        value={adjustmentData.noteVisible}
                        onChange={(e) => setAdjustmentData({ 
                            ...adjustmentData,
                            noteVisible: e.target.value 
                        })}
                        rows={4}
                    />
                </div>

                <div className="app-pto-form-section">
                    <label htmlFor="notesAdmin">Notes (Visible to Admin)</label>
                    <textarea
                        id="notesAdmin"
                        value={adjustmentData.noteAdmin}
                        onChange={(e) => setAdjustmentData({ 
                            ...adjustmentData, 
                            noteAdmin: e.target.value
                        })}
                        rows={4}
                    />
                </div>

                {error && <div className="app-pto-error">{error}</div>}

                <div className="app-pto-form-actions">
                    <button
                        type="submit"
                        className="app-pto-submit-btn"
                        disabled={isSubmitting || (adjustedDays === undefined)}
                        onClick={handleSubmit}
                    >
                        SAVE
                    </button>
                    <button
                        type="button"
                        className="app-pto-cancel-btn"
                        onClick={() => window.history.back()}
                    >
                        CANCEL
                    </button>
                </div>
            </div>
        </div>
    );
};
